import { RecoveryApi, ContactOption } from "../../domain/interfaces/recoveryApi";

export class RecoveryImplementation implements RecoveryApi {
  private getHeaders(token?: string): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };
    if (process.env.REACT_APP_USE_NGROK === 'true') {
      headers['ngrok-skip-browser-warning'] = 'true';
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  async findPatient(tipoDocumento: string, numero: string): Promise<string> {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_RECOVERY_API_URL}/reauth/find/4/${numero}/${tipoDocumento}`,
        { headers: this.getHeaders(), method: "GET" }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || "Paciente no encontrado");
      }

      const data = await response.json();
      return data.key;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error("Error al buscar paciente");
    }
  }

  async getContactOptions(token: string): Promise<ContactOption[]> {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_RECOVERY_API_URL}/reauth/contact`,
        { headers: this.getHeaders(token), method: "GET" }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || "Error al obtener opciones de contacto");
      }

      const data = await response.json();
      
      // Si la respuesta es un array, devolverlo directamente
      if (Array.isArray(data)) {
        return data;
      }
      
      // Si la respuesta es un objeto con una propiedad que contiene el array
      // intentar extraerlo (adaptar según estructura real del backend)
      if (data && typeof data === 'object') {
        // Buscar la propiedad que contiene el array
        const possibleArrayKeys = ['contactos', 'options', 'data', 'items'];
        for (const key of possibleArrayKeys) {
          if (Array.isArray(data[key])) {
            return data[key];
          }
        }
      }
      
      console.error('Respuesta de contactOptions:', data);
      throw new Error("Formato de respuesta inválido");
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error("Error de conexión");
    }
  }

  async sendCode(token: string, option: number): Promise<string> {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_RECOVERY_API_URL}/reauth/sendCode/${option}`,
        { headers: this.getHeaders(token), method: "GET" }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || "Error al enviar código");
      }

      const data = await response.json();
      return data.key;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error("Error de conexión");
    }
  }

  async validateCode(token: string, code: string): Promise<string> {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_RECOVERY_API_URL}/reauth/validar/${code}`,
        { headers: this.getHeaders(token), method: "GET" }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || "Código inválido");
      }

      const data = await response.json();
      return data.key;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error("Error de conexión");
    }
  }

  async changePassword(token: string, password: string): Promise<void> {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_RECOVERY_API_URL}/reauth/changeCode`,
        {
          headers: this.getHeaders(token),
          method: "PATCH",
          body: JSON.stringify({ passwod: password }), // Nota: La API usa "passwod" (typo en backend)
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || "Error al cambiar contraseña");
      }
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error("Error de conexión");
    }
  }
}
